import { type ReactNode } from 'react';

import { COLOR_TOKENS, type ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';

import { Box, type SxProps, type Theme } from '@mui/material';

const RING_GAP_PADDING = 6;
const OUTER_INSET = 2;
const MIN_RING_RADIUS = 1;

export type ConcentricRing = Readonly<{
  id: string;
  pct: number;
  color: ColorToken;
}>;

export type ConcentricRingChartProps = Readonly<{
  /** Ordered innermost ring first. Each ring is an independent gauge, so values need not sum. */
  rings: readonly ConcentricRing[];
  size?: number;
  strokeWidth?: number;
  trackColor?: ColorToken;
  centerContent?: ReactNode;
}>;

/** Nested ring gauges sharing a centre, each showing its own percentage. */
export const ConcentricRingChart = ({
  rings,
  size = 200,
  strokeWidth = 14,
  trackColor = 'Colors/Background/bg-tertiary',
  centerContent,
}: ConcentricRingChartProps) => {
  const center = size / 2;
  const outermost = center - strokeWidth - OUTER_INSET;
  const ringCount = Math.max(rings.length, 1);
  // gap shrinks rather than letting a radius go negative, which SVG silently drops
  const gap = Math.min(
    strokeWidth + RING_GAP_PADDING,
    Math.max((outermost - MIN_RING_RADIUS) / ringCount, 0),
  );
  const innerRadius = Math.max(outermost - (ringCount - 1) * gap, MIN_RING_RADIUS);

  return (
    <Box sx={wrapSx}>
      <Box component="svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {rings.map((ring, index) => {
          const radius = innerRadius + index * gap;
          const circumference = 2 * Math.PI * radius;
          const filled = (Math.min(Math.max(ring.pct, 0), 100) / 100) * circumference;

          return (
            <g key={ring.id}>
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={COLOR_TOKENS[trackColor]}
                strokeWidth={strokeWidth}
              />
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={COLOR_TOKENS[ring.color]}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={`${filled} ${circumference - filled}`}
                transform={`rotate(-90 ${center} ${center})`}
              />
            </g>
          );
        })}
      </Box>
      {centerContent ? <Box sx={centerSx}>{centerContent}</Box> : null}
    </Box>
  );
};

const wrapSx: SxProps<Theme> = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const centerSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
